/**
 * PaymentMethodsAdmin.tsx — Admin > Payments > Payment Methods.
 *
 * The corporate dashboard's Payment Methods page for the signed-in account:
 * the payment types the practice takes, in the Lead Sources layout (see
 * components/admin/AdminList.tsx):
 *   - Name, Classification, Usable For, Surcharge and Loan Terms, by name;
 *     search by name or classification; Show deleted;
 *   - "Add payment method" top right; a click on a live row opens it; Edit /
 *     Delete on a live row, Restore on a deleted one;
 *   - corp's form: Name *, Classification, Usable For, Hint, Surcharge ($ or
 *     %) and Loan Terms (months and APR rows), with corp's checks.
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a live method still opens, read-only, with its loan terms
 * listed but no Add Term or Remove.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { Textarea } from '@/components/ui/Textarea';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { DESCRIPTION_CLASS, DialogField, LABEL_CLASS, fieldClasses } from '@/components/admin/DialogChrome';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  USABLE_FOR_OPTIONS,
  createPaymentMethod,
  deletePaymentMethod,
  formatSurcharge,
  formatTerms,
  listAdminPaymentMethods,
  listPaymentMethodClassifications,
  restorePaymentMethod,
  updatePaymentMethod,
  type AdminPaymentMethod,
  type PaymentMethodClassification,
  type SurchargeType,
} from '@/services/paymentMethodsAdmin.service';
import { toast } from '@/store/toastStore';

const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: 'Name', sortable: true },
  { key: 'classification', header: 'Classification', sortable: true },
  { key: 'usableFor', header: 'Usable For', sortable: true },
  { key: 'surcharge', header: 'Surcharge', className: 'w-28' },
  { key: 'terms', header: 'Loan Terms' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

function methodName(m: AdminPaymentMethod): string {
  return m.name.trim() || `Payment method #${m.paymentMethodID}`;
}

function usableFor(id: number): string {
  return USABLE_FOR_OPTIONS.find((o) => o.value === id)?.short ?? 'Unknown';
}

const SORT_COMPARE: ListSortCompare<AdminPaymentMethod> = {
  name: (a, b) => compareText(methodName(a), methodName(b)),
  classification: (a, b) => compareText(a.classification || 'None', b.classification || 'None'),
  usableFor: (a, b) => compareText(usableFor(a.typeIntentID), usableFor(b.typeIntentID)),
};

export function PaymentMethodsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [methods, setMethods] = useState<AdminPaymentMethod[]>([]);
  const [classifications, setClassifications] = useState<PaymentMethodClassification[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AdminPaymentMethod | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminPaymentMethod | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setMethods(await listAdminPaymentMethods());
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load payment methods'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    listPaymentMethodClassifications()
      .then(setClassifications)
      .catch(() => setClassifications([]));
  }, [load]);

  const query = search.trim().toLowerCase();
  const rows = useMemo(
    () =>
      methods
        .filter(
          (m) =>
            (showDeleted || m.active) &&
            (query === '' || m.name.toLowerCase().includes(query) || m.classification.toLowerCase().includes(query)),
        )
        .sort((a, b) => compareText(methodName(a), methodName(b)) || a.paymentMethodID - b.paymentMethodID),
    [methods, showDeleted, query],
  );
  const sorting = useListSort(rows, { initialKey: 'name', compare: SORT_COMPARE });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  async function handleDelete() {
    if (!deleting) return;
    const { paymentMethodID } = deleting;
    const name = methodName(deleting);
    try {
      await deletePaymentMethod(paymentMethodID);
      setMethods((prev) => prev.map((m) => (m.paymentMethodID === paymentMethodID ? { ...m, active: false } : m)));
      toast.success(`"${name}" deleted`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The payment method could not be deleted.'));
    }
  }

  async function handleRestore(m: AdminPaymentMethod) {
    try {
      await restorePaymentMethod(m.paymentMethodID);
      setMethods((prev) => prev.map((x) => (x.paymentMethodID === m.paymentMethodID ? { ...x, active: true } : x)));
      toast.success('Payment method restored');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The payment method could not be restored.'));
    }
  }

  return (
    <AdminListPage
      title="Payment Methods"
      subtitle="Manage payment methods, surcharges, and loan terms."
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add payment method
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search payment methods..."
          searchLabel="Search payment methods"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="payment-methods-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading payment methods…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No payment methods found' : 'No payment methods yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a payment method to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Payment methods" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((m) => {
              const deleted = !m.active;
              const name = methodName(m);
              return (
                <tr
                  key={m.paymentMethodID}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  onClick={deleted ? undefined : () => setEditing(m)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="payment-method-name">
                      {name}
                    </AdminRowName>
                  </td>
                  <td className={cn(ADMIN_TD_CLASS, 'text-slate-500')}>{m.classification || 'None'}</td>
                  <td className={cn(ADMIN_TD_CLASS, 'text-slate-500')}>{usableFor(m.typeIntentID)}</td>
                  <td className={cn(ADMIN_TD_CLASS, 'text-slate-500 tabular-nums')}>{formatSurcharge(m.surchargeAmount, m.surchargeType)}</td>
                  <td className={cn(ADMIN_TD_CLASS, 'text-slate-500')}>{formatTerms(m.terms)}</td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(m)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setEditing(m)} />
                            <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting(m)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['payment method', 'payment methods']} />
        </>
      )}

      {editing && (
        <PaymentMethodFormModal
          key={editing === 'new' ? 'new' : editing.paymentMethodID}
          method={editing === 'new' ? null : editing}
          classifications={classifications}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${methodName(deleting)}"?` : ''}
        message="It will no longer be offered when recording or charging payments. You can restore it from Show deleted."
        confirmLabel="Delete payment method"
        variant="danger"
      />
    </AdminListPage>
  );
}

// ─── Form ──────────────────────────────────────────────────────────────────────

interface TermRow {
  term: string;
  apr: string;
}

interface MethodForm {
  name: string;
  pmEnumID: number;
  typeIntentID: number;
  hint: string;
  surchargeAmount: string;
  surchargeType: SurchargeType;
  terms: TermRow[];
}

/** Corp's checks: what blocks saving, and what is only suggested. */
export function paymentMethodChecks(f: MethodForm): { errors: string[]; suggestions: string[] } {
  const errors: string[] = [];
  if (f.name.trim() === '') errors.push('Name is required');
  const rows = f.terms.filter((t) => t.term.trim() !== '' || t.apr.trim() !== '');
  if (rows.some((t) => t.term.trim() === '' || t.apr.trim() === '')) errors.push('Every loan term needs both a month count and an APR');
  if (rows.some((t) => t.term.trim() !== '' && !(Number.isInteger(Number(t.term)) && Number(t.term) > 0))) {
    errors.push('Loan term months must be a whole number greater than zero');
  }
  if (rows.some((t) => t.apr.trim() !== '' && !Number.isFinite(Number(t.apr)))) errors.push('Loan term APR must be a number');
  const surcharge = f.surchargeAmount.trim();
  if (surcharge !== '' && !Number.isFinite(Number(surcharge))) errors.push('Surcharge must be a number');

  const suggestions: string[] = [];
  if (f.surchargeType === 'percent' && Number(surcharge) > 100) suggestions.push('Percent surcharge is greater than 100%');
  if (!f.pmEnumID) suggestions.push('No classification selected');
  return { errors, suggestions };
}

function PaymentMethodFormModal({
  method,
  classifications,
  onClose,
  onSaved,
}: {
  method: AdminPaymentMethod | null;
  classifications: PaymentMethodClassification[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = method === null;
  // Locked: AdminFormModal makes it a viewer; the terms lose Add Term and Remove.
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm<MethodForm>(
    () => ({
      name: method?.name ?? '',
      pmEnumID: method?.pmEnumID ?? 0,
      typeIntentID: method?.typeIntentID ?? 0,
      hint: method?.hint ?? '',
      surchargeAmount: method && method.surchargeAmount > 0 ? String(method.surchargeAmount) : '',
      surchargeType: method?.surchargeType ?? 'flat',
      terms: (method?.terms ?? []).map((x) => ({ term: String(x.term), apr: String(x.apr) })),
    }),
    { tracking: !isNew },
  );
  const [saving, setSaving] = useState(false);
  const { errors, suggestions } = paymentMethodChecks(t.form);
  const missingName = t.form.name.trim() === '';

  const classificationOptions = useMemo(() => {
    const out = [{ value: 0, label: 'None' }, ...classifications.filter((c) => c.active).map((c) => ({ value: c.pmEnumID, label: c.title }))];
    // A classification since retired stays visible on the method that uses it.
    if (method && method.pmEnumID > 0 && !out.some((o) => o.value === method.pmEnumID)) {
      out.push({ value: method.pmEnumID, label: method.classification || `Classification #${method.pmEnumID}` });
    }
    return out;
  }, [classifications, method]);

  function setTerm(i: number, next: Partial<TermRow>) {
    t.patch({ terms: t.form.terms.map((row, j) => (j === i ? { ...row, ...next } : row)) });
  }

  async function save() {
    if (errors.length > 0) return;
    setSaving(true);
    const data = {
      name: t.form.name.trim(),
      hint: t.form.hint,
      pmEnumID: t.form.pmEnumID,
      typeIntentID: t.form.typeIntentID,
      surchargeAmount: Number(t.form.surchargeAmount) || 0,
      surchargeType: t.form.surchargeType,
      terms: t.form.terms
        .filter((x) => x.term.trim() !== '' && x.apr.trim() !== '')
        .map((x) => ({ term: Number(x.term), apr: Number(x.apr) })),
    };
    try {
      if (method) {
        await updatePaymentMethod(method.paymentMethodID, data);
        toast.success(`"${data.name}" updated successfully`);
      } else {
        await createPaymentMethod(data);
        toast.success('Payment method created');
      }
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The payment method could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Payment Method' : 'Edit Payment Method'}
      isNew={isNew}
      dirty={isNew ? t.changed.size > 0 : t.dirty}
      saving={saving}
      errors={errors}
      suggestions={suggestions}
      saveLabel={isNew ? 'Create' : 'Save'}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
    >
      <div className="grid gap-4">
        <DialogField
          label="Name"
          htmlFor="payment-method-name"
          required
          state={missingName ? 'error' : 'none'}
          labelHint={missingName ? '— required' : undefined}
          badge={t.badge('name', 'Name')}
        >
          <Input
            id="payment-method-name"
            value={t.form.name}
            placeholder="Payment method name"
            maxLength={255}
            onChange={(e) => t.patch({ name: e.target.value })}
            className={fieldClasses.input(missingName ? 'error' : 'none')}
          />
        </DialogField>
        <div className="grid gap-4 md:grid-cols-2">
          <DialogField label="Classification" htmlFor="payment-method-classification" badge={t.badge('pmEnumID', 'Classification')}>
            <Select<number>
              id="payment-method-classification"
              options={classificationOptions}
              value={t.form.pmEnumID}
              onChange={(v) => t.patch({ pmEnumID: v ?? 0 })}
            />
          </DialogField>
          <DialogField label="Usable For" htmlFor="payment-method-usable-for" badge={t.badge('typeIntentID', 'Usable For')}>
            <Select<number>
              id="payment-method-usable-for"
              options={USABLE_FOR_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              value={t.form.typeIntentID}
              onChange={(v) => t.patch({ typeIntentID: v ?? 0 })}
            />
          </DialogField>
        </div>
        <DialogField label="Hint" htmlFor="payment-method-hint" badge={t.badge('hint', 'Hint')}>
          <Textarea
            id="payment-method-hint"
            rows={3}
            value={t.form.hint}
            placeholder="Shown to staff when this method is selected"
            onChange={(e) => t.patch({ hint: e.target.value })}
          />
        </DialogField>
        <DialogField
          label="Surcharge"
          htmlFor="payment-method-surcharge"
          badge={t.badge('surchargeAmount', 'Surcharge')}
          description={locked ? undefined : 'Leave blank or 0 for no surcharge.'}
        >
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id="payment-method-surcharge"
              value={t.form.surchargeAmount}
              placeholder="0.00"
              inputMode="decimal"
              onChange={(e) => t.patch({ surchargeAmount: e.target.value })}
              wrapperClassName="w-40"
            />
            <label htmlFor="payment-method-surcharge-type" className="sr-only">
              Surcharge type
            </label>
            <Select<SurchargeType>
              id="payment-method-surcharge-type"
              options={[
                { value: 'flat', label: '$ (flat)' },
                { value: 'percent', label: '% (percent)' },
              ]}
              value={t.form.surchargeType}
              onChange={(v) => v && t.patch({ surchargeType: v })}
              wrapperClassName="w-40"
            />
          </div>
        </DialogField>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className={LABEL_CLASS}>
              <span>Loan Terms</span>
              {t.badge('terms', 'Loan Terms')}
            </span>
            {!locked && (
              <Button
                variant="secondary"
                size="xs"
                leftIcon={<Plus className="h-3.5 w-3.5" />}
                onClick={() => t.patch({ terms: [...t.form.terms, { term: '', apr: '' }] })}
              >
                Add Term
              </Button>
            )}
          </div>
          {t.form.terms.length === 0 ? (
            <p className={DESCRIPTION_CLASS}>No loan terms.</p>
          ) : (
            <div className="space-y-2">
              {t.form.terms.map((row, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2" data-testid="loan-term-row">
                  <Input
                    aria-label={`Loan term ${i + 1} months`}
                    value={row.term}
                    placeholder="12"
                    inputMode="numeric"
                    onChange={(e) => setTerm(i, { term: e.target.value })}
                    wrapperClassName="w-20"
                  />
                  <span className="text-sm text-slate-500">months</span>
                  <Input
                    aria-label={`Loan term ${i + 1} APR`}
                    value={row.apr}
                    placeholder="9.99"
                    inputMode="decimal"
                    onChange={(e) => setTerm(i, { apr: e.target.value })}
                    wrapperClassName="w-24"
                  />
                  <span className="text-sm text-slate-500">% APR</span>
                  {!locked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      aria-label={`Remove loan term ${i + 1}`}
                      leftIcon={<Trash2 className="h-4 w-4 text-red-500" />}
                      onClick={() => t.patch({ terms: t.form.terms.filter((_, j) => j !== i) })}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminFormModal>
  );
}
